import type React from "react"
import { sigElementAt, strongestSigLevel } from "utils/tableModel"
import type {
  AxisLeaf,
  ModelCell,
  NumberFormatSpec,
  StatKind,
  TableModel,
} from "utils/tableModel"
import {
  CellRegion,
  CellStyleSpec,
  ResolvedStyle,
  TableStyle,
} from "./tableStyle.types"

/** Points to CSS pixels (96dpi), rounded to 2 decimals: 10.5pt → 14px. */
export const ptToPx = (pt: number): number => Math.round(pt * (4 / 3) * 100) / 100

/** Whether the statistic labels have their own column (stats down) or row (stats across, /T). */
const statLine = (model: TableModel): { column: boolean; row: boolean } => ({
  column: model.statAxis === "down" && model.freeze.x >= 1,
  row: model.statAxis === "across" && model.freeze.y >= 1,
})

/**
 * Region of a grid cell in engine data-array coordinates (`data[y][x]`).
 * The header area is `y < freeze.y`, the stub `x < freeze.x`.
 */
export const getCellRegion = (
  model: TableModel,
  y: number,
  x: number
): CellRegion => {
  const { x: fx, y: fy } = model.freeze
  const stats = statLine(model)
  if (y < fy && x < fx) return "corner"
  if (y < fy) {
    if (stats.row && y === fy - 1) return "statLabel"
    return y === 0 ? "header" : "subHeader"
  }
  if (x < fx) {
    if (stats.column && x === fx - 1) return "statLabel"
    return "stub"
  }
  return "body"
}

const isTotalAt = (
  model: TableModel,
  region: CellRegion,
  y: number,
  x: number
): boolean => {
  const acrossLeaf: AxisLeaf | undefined =
    x >= model.freeze.x ? model.across.leaves[x - model.freeze.x] : undefined
  const downLeaf: AxisLeaf | undefined =
    y >= model.freeze.y ? model.down.leaves[y - model.freeze.y] : undefined
  switch (region) {
    case "header":
    case "subHeader":
      return Boolean(acrossLeaf?.isTotal)
    case "stub":
      return Boolean(downLeaf?.isTotal)
    case "statLabel":
      return Boolean(
        model.statAxis === "across" ? acrossLeaf?.isTotal : downLeaf?.isTotal
      )
    case "body":
      return Boolean(acrossLeaf?.isTotal || downLeaf?.isTotal)
    default:
      return false
  }
}

/** Spreads specs left to right, ignoring undefined values. */
const layerSpecs = (specs: (CellStyleSpec | undefined)[]): CellStyleSpec =>
  specs.reduce<CellStyleSpec>((acc, spec) => {
    if (!spec) return acc
    const defined = Object.fromEntries(
      Object.entries(spec).filter(([, value]) => value !== undefined)
    ) as CellStyleSpec
    return { ...acc, ...defined }
  }, {})

/** The number format a body cell is shown with under this style. */
export const effectiveNumberFormats = (
  style: TableStyle
): Partial<Record<StatKind, NumberFormatSpec>> | undefined =>
  style.numberFormatSource === "style" ? style.numberFormats : undefined

/** The number format a body cell is shown with: the style's or the engine's, with the style's decimals for its kind. */
export const cellNumberFormat = (
  cell: ModelCell,
  style: TableStyle
): NumberFormatSpec | null => {
  const spec = effectiveNumberFormats(style)?.[cell.kind] ?? cell.format
  const decimals = style.numberDecimals?.[cell.kind]
  return spec && decimals !== undefined ? { ...spec, decimals } : spec
}

/** Band of one data row (one down-axis leaf); see `rowBands`. */
export interface RowBand {
  /** The leaf's element: a category with its statistic rows and `sig` row. */
  band: number
  /** The last shown row of its band. */
  lastOfBand: boolean
  /** The last shown row of the table. */
  lastRow: boolean
}

/**
 * Row bands of a model, one per data row (`model.down.leaves`). A band is a
 * run of leaves of one element (`elementIndex`): a category's statistic rows
 * and its `sig` letters row. With statistics across (/T) every row is its
 * own element. `visible[r] === false` (a `sig` row hidden on screen) never
 * ends a band or the table: those flags move to the last shown row.
 */
export const rowBands = (
  model: TableModel,
  visible?: readonly boolean[]
): RowBand[] => {
  const leaves = model.down.leaves
  const bandOf = (r: number): number => leaves[r].elementIndex ?? r
  const shown = (r: number): boolean => visible?.[r] ?? true
  let lastShown = -1
  leaves.forEach((_, r) => {
    if (shown(r)) lastShown = r
  })
  return leaves.map((_, r) => {
    let next = r + 1
    while (next < leaves.length && !shown(next)) next++
    return {
      band: bandOf(r),
      lastOfBand:
        shown(r) && (next >= leaves.length || bandOf(next) !== bandOf(r)),
      lastRow: r === lastShown,
    }
  })
}

/** Bands with every row shown, cached per model (Excel, and callers without a context). */
const allShownBands = new WeakMap<TableModel, RowBand[]>()
const defaultBands = (model: TableModel): RowBand[] => {
  const cached = allShownBands.get(model)
  if (cached) return cached
  const bands = rowBands(model)
  allShownBands.set(model, bands)
  return bands
}

/** Where a cell sits beyond its top-left position; see `resolveCellStyle`. */
export interface CellContext {
  /**
   * Rows the cell covers when merged, in model rows: header rows in the
   * header area, data rows (`model.down.leaves`) below it. Default 1.
   */
  rowSpan?: number
  /**
   * Whether the cell's bottom edge is the bottom of the header block (gets
   * `rules.headerBottom`). Read only for cells at a header-area `y`
   * (`y < freeze.y`). Default: a header-area cell ending on the last header
   * row. Pass `false` for those cells when a column-letters row sits under
   * them. The letters row cells take it only when that row is the last
   * header row: `false` under /T, where the statistic row follows it.
   */
  headerRule?: boolean
  /** Row bands to use (`rowBands` with the rows shown); default: every row shown. */
  bands?: readonly RowBand[]
}

/** A rule is drawn unless it is absent, empty or "none". */
const isRule = (rule: string | undefined): rule is string =>
  rule !== undefined &&
  rule.trim() !== "" &&
  rule.trim().toLowerCase() !== "none"

/**
 * Resolves the style of one grid cell.
 * - `y`, `x` are engine data-array coordinates (`data[y][x]`); for a merged
 *   cell pass its top-left position and its `ctx.rowSpan`.
 * - Each region takes its own section (corner and header: `header`;
 *   subHeader: `header` then `subHeader`; `stub`; `statLabel`; `body` plus
 *   `zebra` on odd row bands); sections do not inherit from each other.
 *   `totals` is layered on for cells of a Totals row/column.
 * - Position rules win over the section's own edge: `rules.top` on the first
 *   row, `rules.headerBottom` under the header block (`ctx.headerRule`),
 *   `rules.bottom` under the last shown data row, else `bandRule` under the
 *   last shown row of a band. A merged cell takes an edge its span reaches.
 * - `surface` "excel" drops the screen-only width and height limits.
 */
export const resolveCellStyle = (
  model: TableModel,
  y: number,
  x: number,
  style: TableStyle,
  surface: "screen" | "excel",
  ctx: CellContext = {}
): ResolvedStyle => {
  const region = getCellRegion(model, y, x)
  const fy = model.freeze.y
  const bands = (): readonly RowBand[] => ctx.bands ?? defaultBands(model)
  const layers: (CellStyleSpec | undefined)[] = []
  switch (region) {
    case "corner":
    case "header":
      layers.push(style.header)
      break
    case "subHeader":
      layers.push(style.header, style.subHeader)
      break
    case "stub":
      layers.push(style.stub)
      break
    case "statLabel":
      layers.push(style.statLabel)
      break
    case "body": {
      const { zebra, ...body } = style.body
      layers.push(body)
      if (zebra && (bands()[y - fy]?.band ?? 0) % 2 === 1) {
        layers.push({ bg: zebra })
      }
      break
    }
  }
  if (isTotalAt(model, region, y, x)) layers.push(style.totals)
  const spec = layerSpecs(layers)

  const rules = style.rules ?? {}
  const lastY = y + Math.max(1, ctx.rowSpan ?? 1) - 1
  let borderTop: string | undefined
  let borderBottom = spec.borderBottom
  if (y === 0 && isRule(rules.top)) borderTop = rules.top
  if (y < fy) {
    const endsHeader = ctx.headerRule ?? lastY === fy - 1
    if (endsHeader && isRule(rules.headerBottom)) {
      borderBottom = rules.headerBottom
    }
  } else {
    const covered = bands().slice(y - fy, lastY - fy + 1)
    if (isRule(rules.bottom) && covered.some((band) => band.lastRow)) {
      borderBottom = rules.bottom
    } else if (
      isRule(style.bandRule) &&
      covered.some((band) => band.lastOfBand)
    ) {
      borderBottom = style.bandRule
    }
  }

  let numberFormat: NumberFormatSpec | null = null
  if (region === "body") {
    const cell = model.cells[y - fy]?.[x - model.freeze.x]
    if (cell) numberFormat = cellNumberFormat(cell, style)
  }

  const screen = surface === "screen"
  return {
    region,
    fontFamily: style.font.family,
    fontSizePt: style.font.sizePt,
    color: spec.color ?? style.font.color,
    ...(spec.bg !== undefined ? { bg: spec.bg } : {}),
    bold: spec.bold ?? false,
    italic: spec.italic ?? false,
    align: spec.align ?? "center",
    ...(spec.border !== undefined ? { border: spec.border } : {}),
    ...(borderTop !== undefined ? { borderTop } : {}),
    ...(borderBottom !== undefined ? { borderBottom } : {}),
    wrap: spec.wrap ?? true,
    ...(screen && spec.minWidth !== undefined
      ? { minWidth: spec.minWidth }
      : {}),
    ...(screen && spec.maxWidth !== undefined
      ? { maxWidth: spec.maxWidth }
      : {}),
    ...(screen && spec.maxHeightEm !== undefined
      ? { maxHeightEm: spec.maxHeightEm }
      : {}),
    numberFormat,
  }
}

/**
 * Layers the significance visuals on a resolved body cell (both surfaces):
 * - the tint of the strongest "higher" result in the cell (its letters or an
 *   upward vs-rest arrow): `sig.upper.fill` or `sig.lower.fill`;
 * - grey (and italic) text for every cell of a low-base banner element.
 * Other regions, and models without significance, come back unchanged.
 * `y`, `x` are engine data-array coordinates, as for `resolveCellStyle`.
 */
export const applySigStyle = (
  resolved: ResolvedStyle,
  model: TableModel,
  y: number,
  x: number,
  style: TableStyle
): ResolvedStyle => {
  if (resolved.region !== "body" || !model.significance) return resolved
  const row = y - model.freeze.y
  const col = x - model.freeze.x
  const level = strongestSigLevel(model.cells[row]?.[col]?.sig)
  const lowBase = sigElementAt(model, row, col)?.lowBase === true
  if (!level && !lowBase) return resolved
  return {
    ...resolved,
    ...(level ? { bg: style.sig[level].fill } : {}),
    ...(lowBase
      ? {
          color: style.lowBase.textColor,
          italic: resolved.italic || style.lowBase.italic,
        }
      : {}),
  }
}

/**
 * Inline CSS for a table cell. Wrapping cells carry their width limits on the
 * cell; one-line cells put them on the content (see `toContentCss`), because
 * table cells ignore max-width.
 */
export const toCss = (r: ResolvedStyle): React.CSSProperties => ({
  fontFamily: r.fontFamily,
  fontSize: `${ptToPx(r.fontSizePt)}px`,
  color: r.color,
  ...(r.bg !== undefined ? { backgroundColor: r.bg } : {}),
  fontWeight: r.bold ? "bold" : "normal",
  fontStyle: r.italic ? "italic" : "normal",
  textAlign: r.align,
  verticalAlign: "middle",
  ...(r.border !== undefined ? { border: r.border } : {}),
  ...(r.borderBottom !== undefined ? { borderBottom: r.borderBottom } : {}),
  ...(r.borderTop !== undefined ? { borderTop: r.borderTop } : {}),
  ...(r.wrap
    ? {
        whiteSpace: "normal",
        wordBreak: "break-word",
        overflowWrap: "anywhere",
        ...(r.minWidth !== undefined ? { minWidth: `${r.minWidth}px` } : {}),
        ...(r.maxWidth !== undefined ? { maxWidth: `${r.maxWidth}px` } : {}),
      }
    : {
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      }),
})

/** Inline CSS for the content wrapper of a one-line cell; undefined when the cell wraps. */
export const toContentCss = (
  r: ResolvedStyle
): React.CSSProperties | undefined =>
  r.wrap
    ? undefined
    : {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        ...(r.minWidth !== undefined ? { minWidth: `${r.minWidth}px` } : {}),
        ...(r.maxWidth !== undefined ? { maxWidth: `${r.maxWidth}px` } : {}),
        ...(r.maxHeightEm !== undefined
          ? { maxHeight: `${r.maxHeightEm}em` }
          : {}),
      }
